/* HCS brand landing pages (v2, 18 Aug 2026).
 *
 * Three jobs:
 *   1. Variant select -> price, compare-at price, image, button state.
 *   2. Re-sort cards inside each band: by rank, then top price down.
 *   3. Chip filters - feed lines on the nutrient pages, wattage on LED.
 *
 * The server already renders every card in a sane order and every product is
 * buyable without this file. If it fails to load the page is still complete
 * and correct, just unsorted and unfiltered.
 *
 * This file does NOT create a #cart-icon-bubble. Only one may exist site-wide;
 * six Shopify scripts find the basket by that id.
 */
(function () {
  'use strict';

  /* ---- 1. variant selection ------------------------------------------- */
  function wireCard(card) {
    var select = card.querySelector('[data-bp-select]');
    if (!select) return;
    var now = card.querySelector('[data-bp-now]');
    var was = card.querySelector('[data-bp-was]');
    var img = card.querySelector('[data-bp-img]');
    var btn = card.querySelector('.hcs-bp__add');

    select.addEventListener('change', function () {
      var opt = select.options[select.selectedIndex];
      if (!opt) return;
      if (now) now.textContent = opt.getAttribute('data-price') || '';
      var compare = opt.getAttribute('data-compare') || '';
      if (was) {
        if (compare) { was.textContent = compare; was.removeAttribute('hidden'); }
        else { was.textContent = ''; was.setAttribute('hidden', ''); }
      }
      var src = opt.getAttribute('data-image');
      if (img && src) { img.setAttribute('src', src); img.removeAttribute('srcset'); }
      var available = opt.getAttribute('data-available') !== 'false' && !opt.disabled;
      if (btn) {
        btn.disabled = !available;
        var label = btn.querySelector('span');
        if (label) label.textContent = available ? 'Add to basket' : 'Sold out';
      }
      try { applySpecs(card); } catch (e) { /* a spec row is never worth breaking the select for */ }
    });
  }

  /* ---- 1b. spec rows follow the variant (6 Sep 2026) ------------------- */
  /* Finbar: "the meta info isn't consistent across sections ... showing the
   * available variants as an info section is useless as the selector is
   * already below. The selector needs to change the info inside the tiles as
   * the variant is changed."
   *
   * The card now emits EVERY spec row (snippets/hcs-brand-unit.liquid) with the
   * original label and value on data-l / data-v, the first four visible, the
   * rest hidden. This pass picks the four to show:
   *
   *   1. rows that only list the variants are dropped - "Available sizes",
   *      "Sizes available", "Flange sizes", "Models" - the select does that job;
   *   2. a row whose LABEL carries a size ("Airflow, 200mm (8\")", "6\" (150mm) x
   *      660mm") is kept only when that size is the chosen variant's, and the
   *      size is cut out of the label;
   *   3. a row whose VALUE lists several sizes ("Airflow by size: 100mm ~205
   *      CFM / 348 m3/h, 150mm ~402 CFM / 683 m3/h, ...") is cut down to the
   *      chosen size's segment;
   *   4. what is left is put in the family's house order - a fan leads with
   *      airflow, noise, power, duct size; a filter with airflow, bed depth,
   *      carbon, flange - so tiles in one band can be read down a column.
   *
   * ⚑ SIZES ARE THE ONLY TOKENS: millimetres and inches, with the duct
   * whitelist mapping inches to millimetres so 10" and 250mm are one size.
   * Anything the parser cannot read is shown as it was - a row is never lost
   * to a failed match, and a single-variant product keeps every row.
   * Lights keep the order the Liquid already gives them (power, PPF,
   * efficacy, fixture size) - only the dropping applies. */
  var DUCT_MM = { 4: 100, 5: 125, 6: 150, 8: 200, 10: 250, 12: 315, 14: 355 };
  var TOKEN_RE = /(\d+(?:\.\d+)?)\s*(mm\b|"|″|”|''|-?inch(?:es)?\b|-?in\b)/gi;
  var DROP_RE = /\bavailable\b|\bsizes\b|\bmodels\b|\bvariants\b|^options\b|sold by|range position|size range/i;

  var DUCT_ALIAS = { 160: 150 };
  var BARE_RE = /(?:^|[\s(\-\/])(100|125|150|160|200|250|315|355)(?=$|[\s)\-\/,:])/g;
  var NXM_RE = /(\d{2,4})\s?x\s?(\d{2,4})(?!\s?(?:mm|cm|m\b|w|"))/gi;
  /* mode: 'label' also reads a bare duct number such as the 150 in "AIR 150 EC";
     'variant' also reads "150x300" - flange by length, unit-less, as filters write it. */
  function sizeTokens(str, mode) {
    var out = [], m;
    if (!str) return out;
    TOKEN_RE.lastIndex = 0;
    while ((m = TOKEN_RE.exec(str))) {
      var n = parseFloat(m[1]), unit = m[2].toLowerCase();
      var key;
      if (unit === 'mm') key = 'mm:' + (DUCT_ALIAS[Math.round(n)] || Math.round(n));
      else key = DUCT_MM[n] ? 'mm:' + DUCT_MM[n] : 'in:' + n;
      out.push({ key: key, start: m.index, end: m.index + m[0].length, text: m[0] });
    }
    if (mode === 'label' && !out.length) {
      BARE_RE.lastIndex = 0;
      while ((m = BARE_RE.exec(str))) {
        var b = parseInt(m[1], 10);
        var st = m.index + m[0].indexOf(m[1]);
        out.push({ key: 'mm:' + (DUCT_ALIAS[b] || b), start: st, end: st + m[1].length, text: m[1] });
      }
    }
    if (mode === 'variant') {
      NXM_RE.lastIndex = 0;
      while ((m = NXM_RE.exec(str))) {
        var a1 = parseInt(m[1], 10), a2 = parseInt(m[2], 10);
        out.push({ key: 'mm:' + (DUCT_ALIAS[a1] || a1), start: m.index, end: m.index + m[1].length, text: m[1] });
        out.push({ key: 'mm:' + a2, start: m.index, end: m.index + m[0].length, text: m[2] });
      }
    }
    return out;
  }
  function keySet(tokens) {
    var s = {};
    tokens.forEach(function (t) { s[t.key] = true; });
    return s;
  }
  function subset(tokens, set) {
    for (var i = 0; i < tokens.length; i++) if (!set[tokens[i].key]) return false;
    return tokens.length > 0;
  }
  /* ---- qualifier tokens (6 Sep 2026) ----
     A row may be keyed to a MODEL rather than a size: "Airflow, T6",
     "Coverage, 9kW Compact Vertical", "Reservoir, With Tank / 4 Pot",
     "Power, 250mm (10\") 2,310 m³/h" where two variants share a duct size.
     Everything after the first comma in the label is the key; its words and
     numbers (sizes and units taken out) must ALL appear in the chosen
     variant's title. Sizes still go through sizeTokens, so "150x600" keeps
     its length test and an airflow figure can never pass as a length. */
  var UNIT_WORDS = { mm: 1, cm: 1, m: 1, 'in': 1, inch: 1, inches: 1, m3: 1, h: 1, hr: 1, hrs: 1, w: 1, kw: 1, v: 1, a: 1, amp: 1, amps: 1, l: 1, ltr: 1, litre: 1, litres: 1, min: 1, minute: 1, x: 1, per: 1, and: 1, or: 1, of: 1, the: 1, cfm: 1, pa: 1, db: 1, dba: 1, hz: 1, ph: 1, ec: 0 };
  function qualTokens(str, mode) {
    var out = [];
    if (!str) return out;
    var spans = sizeTokens(str, mode === 'variant' ? 'variant' : 'label');
    var t = str;
    for (var i = spans.length - 1; i >= 0; i--) t = t.slice(0, spans[i].start) + ' ' + t.slice(spans[i].end);
    t = t.toLowerCase()
      .replace(/³/g, '3')
      .replace(/\b([a-z]{1,4})-(\d)/g, '$1$2')
      .replace(/(\d),(\d{3})/g, '$1$2')
      .replace(/(\d)\s*x\s*(\d)/g, '$1 $2')
      .replace(/(\d)(mm|cm|m3|m|kw|w|v|a|l|in|h|hr|cfm|pa|db|hz)\b/g, '$1 $2');
    var parts = t.split(/[^a-z0-9.]+/);
    var seen = {};
    for (var j = 0; j < parts.length; j++) {
      var w = parts[j].replace(/^\.+|\.+$/g, '');
      if (!w || UNIT_WORDS[w] || seen[w]) continue;
      seen[w] = true;
      out.push({ key: 'q:' + w });
    }
    return out;
  }
  function splitLabel(label) {
    var c = label.indexOf(',');
    if (c < 0) return null;
    return { base: label.slice(0, c), suffix: label.slice(c + 1) };
  }
  /* a label with no comma can still name a model or a figure that is one of
     the variants - "4.5 kW dimensions", "Model S6 airflow". Only tokens that
     carry a digit AND appear in some variant title count, so a plain word such
     as "Power" is never mistaken for a key. */
  function optTitle(opt) {
    var t = opt.textContent || '';
    var i = t.lastIndexOf('—');
    return (i >= 0 ? t.slice(0, i) : t).trim();
  }
  /* the first one or two words of a variant title, letters only:
     "Single Outlet (1.6L / minute)" -> ["single outlet", "single"] */
  function leadPhrases(title) {
    var words = (title || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').trim().split(/\s+/);
    var out = [];
    if (words.length >= 2 && /^[a-z]+$/.test(words[0]) && /^[a-z]+$/.test(words[1])) out.push(words[0] + ' ' + words[1]);
    if (words.length >= 1 && /^[a-z]{3,}$/.test(words[0])) out.push(words[0]);
    return out;
  }
  function prefixKeys(label, allQual) {
    var out = [];
    qualTokens(label, 'label').forEach(function (t) {
      var w = t.key.slice(2);
      if (!/\d/.test(w) || !allQual[t.key]) return;
      /* "1" or "12" alone is a count, not a model; 4.5 / 2000 / t4 / hd90 are */
      if (/^\d{1,2}$/.test(w)) return;
      out.push({ key: t.key, word: w });
    });
    return out;
  }
  function stripWords(label, keys) {
    var out = label;
    keys.forEach(function (k) {
      var pat = k.word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/(\d)(?=\d)/g, '$1,?');
      out = out.replace(new RegExp('(^|[^a-z0-9])' + pat + '(?:\\s*(?:mm|cm|m3|m³|m|kw|w|v|a|l|in|h|hr|cfm|pa|db|hz))?(?![a-z0-9])', 'i'), '$1 ');
    });
    return cleanEdges(out);
  }
  function stripTokens(str, tokens) {
    var out = str;
    for (var i = tokens.length - 1; i >= 0; i--) out = out.slice(0, tokens[i].start) + out.slice(tokens[i].end);
    return cleanEdges(out);
  }
  function cleanEdges(str) {
    return str
      .replace(/\(\s*[\/,;·|\-–—\s]*\)/g, ' ')
      .replace(/^[\s,;:/·|)(x×\-–—]+|[\s,;:/·|(\-–—]+$/g, '')
      .replace(/^(?:and|or|on|for|the)\s+/i, '')
      .replace(/\s+(?:and|or|on)$/i, '')
      .replace(/^[\s,;:/·|)(\-–—]+|[\s,;:/·|(\-–—]+$/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }
  function tidyLabel(label) {
    var l = label.replace(/\(?\s*by\s+(?:size|model|duct|diameter)s?\s*\)?/ig, '')
      .replace(/\b(?:models?|kit|version|variant)\b/ig, '')
      .replace(/\bs$/i, '');
    l = cleanEdges(l).replace(/\blengths\b/i, 'length');
    if (!l) return '';
    return l.charAt(0).toUpperCase() + l.slice(1);
  }
  function looksLikeAirflow(v) {
    return /m\s?(?:3|³)\s?\/\s?h|cfm/i.test(v);
  }
  /* a multi-size value is cut into one segment per size token */
  function segmentFor(value, want) {
    var toks = sizeTokens(value);
    if (toks.length < 2) return null;
    var firstDigit = value.search(/\d/);
    var tokenFirst = toks[0].start <= firstDigit;
    var segs = [];
    for (var i = 0; i < toks.length; i++) {
      var a, b;
      if (tokenFirst) { a = toks[i].start; b = i + 1 < toks.length ? toks[i + 1].start : value.length; }
      else { a = i > 0 ? toks[i - 1].end : 0; b = toks[i].end; }
      segs.push({ tok: toks[i], text: value.slice(a, b) });
    }
    /* neighbouring tokens that name the same size ("6\" / 150mm") belong to one segment */
    var merged = [];
    segs.forEach(function (sg) {
      var last = merged[merged.length - 1];
      if (last && last.keys[sg.tok.key] && sg.text.replace(sg.tok.text, '').replace(/[\s\/(),]/g, '') === '') {
        last.text += sg.text; return;
      }
      if (last && last.keys[sg.tok.key]) { last.text += sg.text; return; }
      var keys = {}; keys[sg.tok.key] = true;
      merged.push({ keys: keys, text: sg.text });
    });
    /* every segment must carry a figure of its own - "3-speed switch on
       6-inch and 8-inch models" mentions sizes but lists nothing per size */
    var NOISE = /^(?:and|or|&|,|;|\/)?$|^(?:depending|by|across|for|on)\b/i;
    var withFigure = 0, pure = 0;
    for (var q = 0; q < merged.length; q++) {
      var body = stripTokens(merged[q].text, sizeTokens(merged[q].text));
      if (/\d/.test(body)) withFigure++;
      else if (NOISE.test(body)) pure++;
      else return null;
    }
    /* a list is either sizes with figures or a bare list of sizes - a mix is
       prose ("3-speed switch on 6-inch and 8-inch models") */
    if (withFigure && pure) return null;
    for (var j = 0; j < merged.length; j++) {
      var hit = false;
      for (var k in merged[j].keys) if (want[k]) hit = true;
      if (hit) {
        var cut = stripTokens(merged[j].text, sizeTokens(merged[j].text));
        /* a list of sizes: the size itself is the answer ("660mm"), not the
           connective that follows it */
        if (!cut || NOISE.test(cut)) return cleanEdges(merged[j].text.replace(/\s+(?:depending|by|across|for)\b.*$/i, ''));
        return cut;
      }
    }
    return null;
  }
  /* "Power: 40W / 75W / 150W / 320W" - one figure per variant, in variant order, no sizes named */
  function positional(value, select) {
    if (!select || !/ \/ /.test(value)) return null;
    var parts = value.split(/\s\/\s/);
    if (parts.length !== select.options.length) return null;
    var p = parts[select.selectedIndex];
    return p ? cleanEdges(p) : null;
  }

  var FAMILY_ORDER = {
    fan:        [['airflow', 'm3/h', 'm³/h', 'cfm', 'extraction'], ['noise', 'db', 'sound', 'quiet'], ['power', 'watt', 'draw', 'consumption'], ['duct', 'flange', 'inlet', 'spigot', 'diameter', 'connection', 'fan size'], ['speed', 'control', 'dimm'], ['static', 'pressure'], ['motor', 'impeller'], ['housing', 'material', 'construction', 'body'], ['warranty', 'life']],
    filter:     [['airflow', 'm3/h', 'm³/h', 'cfm'], ['bed'], ['carbon'], ['flange', 'duct', 'inlet'], ['length', 'dimension', 'size', 'body'], ['pre-filter', 'prefilter', 'sleeve'], ['weight'], ['warranty', 'life', 'lasts']],
    controller: [['control', 'function', 'mode', 'program'], ['sensor', 'probe', 'measure'], ['load', 'output', 'rated', 'max'], ['display', 'screen', 'app', 'connectiv'], ['compatib', 'suits', 'fits', 'works with'], ['connect', 'cable', 'lead'], ['mount', 'install'], ['power', 'voltage', 'supply']],
    climate:    [['extraction', 'output', 'capacity', 'btu', 'kw', 'cooling', 'heating', 'rate', 'humidif', 'moisture'], ['coverage', 'room', 'area', 'suits', 'tent'], ['power', 'watt', 'draw', 'consumption'], ['tank', 'reservoir', 'water', 'drain'], ['operating', 'range'], ['noise', 'db'], ['control', 'mode', 'hygrostat', 'thermostat'], ['dimension', 'size'], ['weight'], ['refrigerant'], ['warranty']],
    meter:      [['measures', 'measure', 'parameter', 'reads'], ['range'], ['accura', 'resolution'], ['calibrat'], ['waterproof', 'ip rating', 'ip'], ['display'], ['power', 'battery'], ['probe', 'sensor', 'electrode']],
    pump:       [['output', 'flow', 'l/h', 'litre', 'rate', 'gpd', 'produces'], ['outlet'], ['power', 'watt'], ['pressure'], ['membrane', 'removal', 'stage'], ['noise', 'db'], ['connection', 'fits', 'hose', 'tubing']],
    system:     [['system type', 'type', 'system'], ['pot', 'plant site', 'sites', 'plants'], ['reservoir', 'volume', 'tank', 'capacity'], ['footprint', 'dimension', 'size'], ['pump'], ['pipework', 'pipe'], ['media', 'medium']],
    tool:       [['type'], ['throughput', 'capacity'], ['blade'], ['power', 'motor'], ['dimension', 'size'], ['weight'], ['material', 'construction'], ['warranty']]
  };
  function familyOf(card) {
    var t = (card.getAttribute('data-bp-type') || '').toLowerCase();
    if (card.getAttribute('data-bp-light') === '1') return 'light';
    if (card.getAttribute('data-bp-fan') || /extractor|inline fan|box fan|fans\b|extraction/.test(t)) return 'fan';
    if (/filter/.test(t) && /carbon|odour|odor/.test(t)) return 'filter';
    if (/controller|trolmaster|thermostat/.test(t)) return 'controller';
    if (/dehumid|humidif|humidity|air con|heater|chiller|temperature|drying|curing|co2|gas/.test(t)) return 'climate';
    if (/meter|ph & nutrient|monitor/.test(t)) return 'meter';
    /* pumps and RO units before systems: their type reads "Hydroponics & Systems > ..." */
    if (/pump|aeration|reverse osmosis|\bro\b|water filter/.test(t)) return 'pump';
    if (/deep water|easyfeed|easy feed|system|autopot|iws/.test(t)) return 'system';
    if (/trimm/.test(t)) return 'tool';
    if (/filter/.test(t)) return 'filter';
    return 'generic';
  }
  function rankOf(order, label, value) {
    if (!order) return 50;
    var l = label.toLowerCase(), v = value.toLowerCase();
    /* "Airflow with filter" sits just behind the bare airflow figure */
    var late = /\bwith\b|pressure drop/.test(l) ? 0.5 : 0;
    for (var i = 0; i < order.length; i++) {
      var words = order[i];
      for (var j = 0; j < words.length; j++) {
        if (l.indexOf(words[j]) !== -1) return i + late;
      }
    }
    /* an airflow figure with a bare size label counts as airflow */
    if (order === FAMILY_ORDER.fan || order === FAMILY_ORDER.filter) {
      if (looksLikeAirflow(v)) return 0;
    }
    return 50;
  }

  /* ⚑ PER-SIZE SPECS (19 Oct 2026). A tile rendered with data-bp-vspec has
   * four fixed rows filled from the chosen option's data-sp-* figures - one
   * size per line, same four rows on every tile, so the spacing never moves.
   * The card's sortable figures (data-bp-watt / -ppf / -dim) follow the size
   * too, so a sort or a filter reads the size on screen. */
  function applyVspec(card, list) {
    var select = card.querySelector('[data-bp-select]');
    var opt = select && select.options[select.selectedIndex];
    if (!opt) return;
    var w = opt.getAttribute('data-sp-w'), p = opt.getAttribute('data-sp-ppf'),
        dw = opt.getAttribute('data-sp-dimw');
    /* Each row's text is pre-formatted per size by the theme (data-spt-<key>,
     * from snippets/hcs-vspec-text.liquid) - the same text the product-page
     * table prints, so tile and table cannot disagree (21 Oct 2026). */
    Array.prototype.forEach.call(list.querySelectorAll('li[data-k]'), function (li) {
      var span = li.querySelector('span');
      if (span) span.textContent = opt.getAttribute('data-spt-' + li.getAttribute('data-k')) || '\u2013';
    });
    var set = function (a, v) { if (v && v !== '0') card.setAttribute(a, v); else card.removeAttribute(a); };
    set('data-bp-watt', w);
    if (opt.hasAttribute('data-sp-ppf')) { set('data-bp-ppf', p); set('data-bp-dim', dw); }
    ['flow', 'aout', 'air'].forEach(function (k) {
      if (opt.hasAttribute('data-sp-' + k)) set('data-bp-' + k, opt.getAttribute('data-sp-' + k));
    });
  }

  function applySpecs(card) {
    var vlist = card.querySelector('[data-bp-vspec]');
    if (vlist) { applyVspec(card, vlist); return; }
    var rows = Array.prototype.slice.call(card.querySelectorAll('li[data-bp-spec]'));
    if (!rows.length) return;
    var select = card.querySelector('[data-bp-select]');
    var want = {};
    var allQual = {};
    var leadKeys = [];
    var wantLead = {};
    var wantCount = 0;
    if (select) {
      var opt = select.options[select.selectedIndex];
      /* the option reads "<variant> — £price"; cut at the LAST dash, since a
         variant title may carry one of its own ("NW2 — 110 L/h · 1 Outlet") */
      var vt = opt ? optTitle(opt) : '';
      sizeTokens(vt, 'variant').forEach(function (t) { want[t.key] = true; wantCount++; });
      qualTokens(vt, 'variant').forEach(function (t) { want[t.key] = true; wantCount++; });
      var leadCount = {};
      for (var oi = 0; oi < select.options.length; oi++) {
        var ot = optTitle(select.options[oi]);
        qualTokens(ot, 'variant').forEach(function (t) { allQual[t.key] = true; });
        leadPhrases(ot).forEach(function (ph) { leadCount[ph] = (leadCount[ph] || 0) + 1; });
      }
      /* a leading phrase shared by SOME variants but not all names a model:
         "Single Outlet", "With Tank" - never "Opticlimate Pro" when every
         option starts with it */
      for (var ph in leadCount) if (leadCount[ph] < select.options.length) leadKeys.push(ph);
      leadKeys.sort(function (a, b) { return b.length - a.length; });
      leadPhrases(vt).forEach(function (ph) { wantLead[ph] = true; });
    }
    var family = familyOf(card);
    var order = FAMILY_ORDER[family];
    var kept = [];
    rows.forEach(function (li, idx) {
      var label = li.getAttribute('data-l') || '';
      var value = li.getAttribute('data-v') || '';
      var lt = sizeTokens(label, family === 'fan' || family === 'filter' ? 'label' : '');
      var sl = splitLabel(label);
      var lq = sl ? qualTokens(sl.suffix, 'label') : [];
      var pk = (!sl && wantCount) ? prefixKeys(label, allQual) : [];
      if (pk.length) lq = pk;
      var leadHit = null;
      if (!sl && !pk.length && wantCount) {
        var ll = label.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
        for (var li2 = 0; li2 < leadKeys.length; li2++) {
          if (ll === leadKeys[li2] || ll.indexOf(leadKeys[li2] + ' ') === 0) { leadHit = leadKeys[li2]; break; }
        }
        if (leadHit) {
          if (!wantLead[leadHit]) return;                       /* another model's row */
          lq = [{ key: 'lead:' + leadHit }];
          specificityBonus = 1;
        }
      }
      var specificity = 0, specificityBonus = 0;
      var showLabel = label, showValue = value;
      if (!lt.length && !lq.length && DROP_RE.test(label)) return;   /* 1. a list of the variants */
      if ((lt.length || lq.length) && wantCount) {                    /* 2. a size- or model-specific row */
        if (lt.length && !subset(lt, want)) return;
        if (lq.length && !leadHit && !subset(lq, want)) return;
        specificity = lt.length + lq.length + specificityBonus;
        if (sl) showLabel = tidyLabel(sl.base);
        else if (pk.length) showLabel = tidyLabel(stripWords(stripTokens(label, lt), pk));
        else if (leadHit) showLabel = tidyLabel(cleanEdges(label.replace(new RegExp('^\\s*' + leadHit.replace(/\s+/g, '\\s+'), 'i'), '')));
        else showLabel = tidyLabel(stripTokens(label, lt));
        if (leadHit && !showLabel) showLabel = 'This model';
        /* a model code left behind ("AIR - EC") is not a label */
        if (showLabel && (showLabel.replace(/[^a-z]/g, '').length < 3)) showLabel = '';
        if (!showLabel) showLabel = looksLikeAirflow(value) && !/[,;]/.test(value) ? 'Airflow' : (lt.length ? 'This size' : 'This model');
      } else if (!lt.length && wantCount) {                    /* 3. one value listing several sizes */
        var seg = segmentFor(value, want);
        if (seg) { showValue = seg; showLabel = tidyLabel(label) || label; }
        else {
          var pos = positional(value, select);                 /* 3b. one figure per variant, in order */
          if (pos) showValue = pos;
        }
      }
      kept.push({ li: li, idx: idx, label: showLabel, value: showValue, spec: specificity, rank: rankOf(order, showLabel, showValue) });
    });
    /* the same label twice - "Power" for a plain 10\" row and for the 10\" High
       row - keeps only the most specific match */
    var best = {};
    function dupKey(row) {
      var l = row.label.toLowerCase();
      /* a "This size" row holding an airflow figure is the airflow row */
      if (/^this (?:size|model)$/.test(l) && looksLikeAirflow(row.value) && !/[,;]/.test(row.value)) return 'airflow';
      return l.replace(/\bby\s+(?:size|model|duct|diameter)s?\b/g, '').replace(/\b(?:models?|kit|version|variant|size|sizes|carbon|max|maximum|range)\b/g, '').replace(/[^a-z0-9]/g, '');
    }
    function dupScore(row) { return row.spec * 10 + (/^this (?:size|model)$/i.test(row.label) ? 0 : 1); }
    kept.forEach(function (row) {
      var k = dupKey(row);
      if (!(k in best) || dupScore(row) > dupScore(kept[best[k]])) best[k] = kept.indexOf(row);
    });
    kept = kept.filter(function (row, i) { return best[dupKey(row)] === i; });
    /* a compound "This size" line (airflow, watts, pascals in one value) is
       only the lead when nothing better leads */
    var hasLead = kept.some(function (r) { return r.rank === 0 && !/^this (?:size|model)$/i.test(r.label); });
    if (hasLead) kept.forEach(function (r) { if (/^this (?:size|model)$/i.test(r.label) && r.rank === 0) r.rank = 40; });
    if (order) kept.sort(function (a, b) { return a.rank - b.rank || a.idx - b.idx; });
    var list = rows[0].parentNode;
    rows.forEach(function (li) { li.setAttribute('hidden', ''); });
    kept.forEach(function (row, i) {
      var li = row.li;
      var strong = li.querySelector('strong'), span = li.querySelector('span');
      if (strong) strong.textContent = row.label;
      if (span) span.textContent = row.value.length > 52 ? row.value.slice(0, 49) + '...' : row.value;
      if (i < 4) li.removeAttribute('hidden'); else li.setAttribute('hidden', '');
      list.appendChild(li);                                    /* house order = DOM order */
    });
    /* the tent size row (li.is-size) and any fallback rows are not data-bp-spec and stay where they are */
    var sizeRow = list.querySelector('li.is-size');
    if (sizeRow) list.insertBefore(sizeRow, list.firstChild);
  }

  /* ---- 2. ordering ----------------------------------------------------- */
  /* ⚑ NO AVAILABILITY IN THE ORDER. Removed on Finbar's instruction,
   * 7 Sep 2026: "no availability sort anwhere". Until then an out-of-stock
   * product was pushed below every in-stock one in every band, whatever it
   * was sorted by. It is now ordered on its merits like any other card and
   * can lead a band. `data-bp-available` is still written on the card and
   * still drives the "Out of stock" label - it just no longer moves anything.
   * Do not reintroduce it here or in hcs-bp-controls.js. */
  function sortGrid(grid) {
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-bp-card]'));
    if (cards.length < 2) return;
    cards.sort(function (a, b) {
      /* How well it actually sells. Rank 500 is "not named", so an unranked
       * product sits between the bestsellers and anything forced to the end. */
      var aR = parseInt(a.getAttribute('data-bp-rank'), 10) || 500;
      var bR = parseInt(b.getAttribute('data-bp-rank'), 10) || 500;
      if (aR !== bR) return aR - bR;
      return (parseInt(b.getAttribute('data-bp-max'), 10) || 0) -
             (parseInt(a.getAttribute('data-bp-max'), 10) || 0);
    });
    /* ⚑ THE TOP LINE OF A PARENT PAGE MIXES TYPES (Finbar, 6 Sep 2026: "make
     * sure a few different types of products are also shown so it's a good
     * top line"). Within the first N cards no sub-category takes more than
     * three places: walking down the best-selling order, a card whose type
     * already has three in the top N is deferred to just after it. Nothing is
     * dropped, and beyond the top N the order is the sellers' order. Staples
     * (rank 1..3) sit first and count toward their type's three. */
    var divN = parseInt(grid.getAttribute('data-bp-diversify'), 10) || 0;
    if (divN > 0 && cards.length > divN) {
      var cap = 3, seen = {}, top = [], held = [], rest = [];
      cards.forEach(function (c) {
        if (top.length >= divN) { rest.push(c); return; }
        var k = (c.getAttribute('data-bp-subcat') || c.getAttribute('data-bp-type') || '').toLowerCase();
        if (k && (seen[k] || 0) >= cap) { held.push(c); return; }
        seen[k] = (seen[k] || 0) + 1;
        top.push(c);
      });
      cards = top.concat(held, rest);
    }
    var frag = document.createDocumentFragment();
    cards.forEach(function (c) { frag.appendChild(c); });
    grid.appendChild(frag);
  }

  /* ---- 3. chip filters ------------------------------------------------- */
  /* Shared by the feed-line chips and the LED wattage chips. Anything the
   * test cannot read STAYS VISIBLE - a product is never hidden because a
   * parser or a handle match failed. */
  function wireChips(bar, cardSel, test) {
    if (!bar) return;
    var chips = Array.prototype.slice.call(bar.querySelectorAll('[data-line], [data-w], [data-med]'));
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        var val = chip.getAttribute('data-line') || chip.getAttribute('data-w') || chip.getAttribute('data-med');
        document.querySelectorAll(cardSel).forEach(function (card) {
          card.style.display = test(card, val) ? '' : 'none';
        });
      });
    });
  }

  function wattOf(card) {
    var t = card.querySelector('.hcs-bp__card-title');
    if (!t) return null;
    /* Digits must be immediately followed by W, so this cannot lift "2304"
     * out of "2304 umol/s" - that is a PPF figure, not a wattage. */
    var m = /(\d{2,4})\s*w\b/i.exec(t.textContent);
    return m ? parseInt(m[1], 10) : null;
  }

  function boot(root) {
    var r = root || document;
    r.querySelectorAll('[data-bp-card]').forEach(wireCard);
    r.querySelectorAll('[data-bp-card]').forEach(function (card) { try { applySpecs(card); } catch (e) {} });
    r.querySelectorAll('[data-bp-sortgrid]').forEach(sortGrid);

    /* THE MEDIUM CHIPS ARE NO LONGER WIRED HERE (19 Aug 2026).
     *
     * They used to hide cards by setting `style.display` directly. The new
     * filter tray in hcs-bp-controls.js hides cards with the `hidden`
     * attribute, and it MOVES them between containers when a sort collapses
     * the bands. Two mechanisms writing visibility on the same nodes is a
     * silent fight - a card hidden by a chip would reappear the moment the
     * tray re-ran, and a card the tray had hidden could not be un-hidden by
     * clearing a chip.
     *
     * The chips still work. hcs-bp-controls.js now owns them and treats a chip
     * as a shortcut into the Growing medium facet, so the rail and the tray
     * always agree. wireChips and wattOf are left in this file unused and
     * harmless rather than deleted, because the AC Infinity page is a separate
     * live Google Ads landing page and nothing here should be pulled out
     * without checking it. */

    /* The wattage chips are gone - the LED page is banded by wattage server
     * side, so they duplicated the bands and their anchors threw the reader
     * to the "Other fixtures" band at the foot of the page. */
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else { boot(document); }

  /* The section re-boots on section load, or changing anything in the theme
   * customiser leaves the page inert until a manual reload. */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  /* Parent collection pages (6 Sep 2026): hcs-bp-controls.js fetches pages
   * 2..n of a 700-product collection and appends their cards. It calls this on
   * the NEW cards only, before moving them into the grid, so nothing on the
   * page is wired twice. */
  window.hcsBrandBoot = boot;
  window.hcsBrandSortGrid = sortGrid;
})();
